/*eslint @typescript-eslint/no-empty-function: "off"*/
import React, { RefObject } from 'react';
import { DeleteState } from 'store/entity/types';
import { List } from './styles';
import { display } from 'components/ErrorView/ErrorDialog';
import TaskListView from 'components/TaskListView';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { mergeRefs } from 'utils/happyFunctions';
import { IAssignmentView, ITask } from 'store/tasks/tasksType';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { WindowSize } from '../../../components/TaskListView/TaskListView';
import store, { RootState } from '../../../store/store';
import { black, gray, orange } from '../../../utils/style/colors';
import Icon from '../../../components/Icon';
import i18n from '../../../i18n';

interface IComponentProps {
  tasks: Array<ITask>;
  loading: boolean;
  thisUser: IThisUserState;
  companyId: number;
  projectId?: number;
  onUpdateTask: (taskView: ITask) => void;
  requireTasks: (deleteState?: DeleteState) => Promise<any>;
  showProgress: boolean;
  showWeight: boolean;
  taskSelected: number | boolean;
  companyOwnerId: number;
  allTasksFetched: boolean;
  unfilteredTasks: ITask;
  templateSelected: boolean;
  widthMultiplier?: number;
  handleClickOnTask: (taskId: number) => void;
  onUpdateAssignment: (assignment: IAssignmentView) => void;
}

interface IComponentState {
  loading: boolean;
  requestSent: boolean;
  loadMoreButton: boolean;
  taskListMaxHeight: number | null;
  taskSize: WindowSize;
}

class TasksList extends React.Component<IComponentProps, IComponentState> {
  taskList: RefObject<HTMLUListElement> = React.createRef();
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      loading: false,
      requestSent: false,
      loadMoreButton: true,
      taskListMaxHeight: null,
      taskSize: this.findWindowSize(),
    };
  }
  findWindowSize = () => {
    const state = store.getState() as RootState;
    const isSidebarCollapsed = state.app.UI.sidebar.isSideBarCollapsed;
    const sideBarOffset = !isSidebarCollapsed
      ? 235
      : window.innerWidth < 800
        ? 52
        : 60;
    const validWidth = this.props.widthMultiplier
      ? window.innerWidth * (this.props.widthMultiplier || 1)
      : window.innerWidth - sideBarOffset;
    return validWidth > 900 ? 'large' : validWidth > 700 ? 'medium' : 'small';
  };

  handleResize = () => {
    this.setState({
      taskSize: this.findWindowSize(),
    });
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    if (this.taskList.current) {
      this.taskList.current.addEventListener('scroll', this.handleOnScroll);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.taskList.current) {
      this.taskList.current.removeEventListener('scroll', this.handleOnScroll);
    }
  }

  handleOnScroll = () => {
    if (this.taskList.current) {
      const boundary = 10;
      const scrollTop =
        this.taskList.current.scrollTop || document.body.scrollTop;
      const scrollHeight =
        this.taskList.current.scrollHeight || document.body.scrollHeight;
      const clientHeight =
        this.taskList.current.clientHeight || window.innerHeight;
      const scrolledToBottom =
        Math.ceil(scrollTop + clientHeight) >= scrollHeight - boundary;

      if (scrolledToBottom) {
        if (!this.props.allTasksFetched) {
          if (!this.state.requestSent) {
            this.loadData();
          }
        }
      }
    }
  };

  updateTaskListHeight() {
    if (this.taskList.current) {
      const maxHeight =
        this.taskList.current.closest('section')?.getBoundingClientRect()
          .height || null;
      this.setState({ taskListMaxHeight: maxHeight });
    }
  }

  loadData = () => {
    this.setState({ requestSent: true, loading: true });
    this.props.requireTasks().then(
      () => {
        this.setState({
          loading: false,
          requestSent: false,
          loadMoreButton: false,
        });
      },
      (err: any) => {
        display(err);
        this.setState({ loadMoreButton: true, loading: false });
      }
    );
  };

  render() {
    const {
      tasks,
      loading,
      thisUser,
      companyId,
      projectId,
      taskSelected,
      onUpdateTask,
      showProgress,
      companyOwnerId,
      unfilteredTasks,
      templateSelected,
      handleClickOnTask,
      onUpdateAssignment,
      showWeight,
    } = this.props;

    const { requestSent, loadMoreButton } = this.state;
    return (
      // @ts-expect-error JSX error
      <DragDropContext onDragEnd={() => {}}>
        <Droppable droppableId="droppable-1" isDropDisabled={true}>
          {(provided) => (
            <List
              ref={mergeRefs(this.taskList, provided.innerRef)}
              style={{
                maxHeight: this.state.taskListMaxHeight
                  ? `${this.state.taskListMaxHeight}px`
                  : undefined,
              }}
              {...provided.droppableProps}
            >
              {Object.values(tasks || []).map((task, key) => {
                return (
                  <TaskListView
                    key={task.id}
                    task={task}
                    active={taskSelected === task.id}
                    template={unfilteredTasks[task.templateId]}
                    thisUser={thisUser}
                    companyId={companyId}
                    projectId={projectId}
                    thisUserId={Number(thisUser.userId)}
                    onUpdateTask={onUpdateTask}
                    showProgress={showProgress}
                    onClickOnTask={handleClickOnTask}
                    companyOwnerId={companyOwnerId}
                    templateSelected={templateSelected}
                    onUpdateAssignment={onUpdateAssignment}
                    showWeight={showWeight}
                    index={key!}
                    editable={false}
                    windowSize={this.state.taskSize}
                  />
                );
              })}
              {tasks.length === 0 && !loading ? (
                <p
                  style={{
                    padding: '10px 20px',
                    color: gray,
                    maxWidth: '500px',
                    margin: '50px auto',
                  }}
                >
                  <>
                    <Icon
                      type="info"
                      color={orange}
                      style={{ paddingInlineEnd: '10px' }}
                    />
                    {i18n.t('noTaskFoundPleaseCheckFilters1', {
                      ns: 'taskManager',
                    })}
                    <Icon type="sliders" color={black} />
                    {i18n.t('noTaskFoundPleaseCheckFilters2', {
                      ns: 'taskManager',
                    })}
                  </>
                </p>
              ) : (
                <></>
              )}
              {requestSent && !loadMoreButton && (
                <div className="progress">
                  <div className="indeterminate" />
                </div>
              )}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    );
  }
}

export default TasksList;
